import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  // When this value changes the boundary clears its error and re-renders its
  // children. AdminLayout passes the current pathname so navigating to another
  // screen automatically recovers from a crashed one (no manual retry needed).
  resetKey?: unknown;
}

interface ErrorBoundaryState {
  error: Error | null;
}

// Last-resort safety net so a render-time crash in one screen degrades to a
// contained fallback instead of white-screening the whole SPA (#root goes
// blank). The fallback text is hard-coded Korean rather than i18n so it can
// still render even when the failure is in the i18n layer itself.
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidUpdate(prev: ErrorBoundaryProps) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface in the console for diagnosis; a real telemetry sink can hook here.
    console.error('[ErrorBoundary] render crash:', error, info.componentStack);
  }

  private handleRetry = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          margin: 24,
          padding: 24,
          border: '1px solid var(--destructive)',
          borderRadius: 8,
          background: '#FEE2E8',
          color: 'var(--destructive)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <strong style={{ fontSize: 16 }}>화면을 표시하는 중 오류가 발생했습니다.</strong>
        <span style={{ fontSize: 14 }}>
          잠시 후 다시 시도하거나 다른 메뉴로 이동해 주세요.
        </span>
        <button type="button" className="btn btn-sm btn-outline" onClick={this.handleRetry}>
          다시 시도
        </button>
      </div>
    );
  }
}
